import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../LanguageContext';
import styles from './Projects.module.css';
import { projectsConfig } from './projectsConfig';
import { loadThumbnail, loadProjectGallery } from './imageLoader';
import LazyImage from './LazyImage';

// מגזינים שבהם הופיעו פרויקטים
const projectFeaturedIn = {
  1: ['Forbes Israel', 'Archilovers'], // בית פרטי בשילת
  4: ['וואלה בית ועיצוב'], // פנטהאוז תל אביבי
};

const MoreProjects = () => {
  const { t, isEnglish } = useTranslation();
  const sectionRef = useRef(null);
  const modalRef = useRef(null);
  const thumbnailRefs = useRef([]);

  // State
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectGallery, setProjectGallery] = useState([]);
  const [isLoadingGallery, setIsLoadingGallery] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [visibleThumbnails, setVisibleThumbnails] = useState(new Set());

  // WhatsApp link generator
  const getWhatsAppLink = (projectTitle = '') => {
    const phoneNumber = '972544998929';
    const message = isEnglish
      ? (projectTitle
          ? `Hi Narkis, I loved the project "${projectTitle}", I'd love to hear more about...`
          : 'Hi Narkis, I would love a consultation')
      : (projectTitle
          ? `היי נרקיס, אהבתי את הפרוייקט "${projectTitle}", אשמח לשמוע עוד על ...`
          : 'היי נרקיס, אשמח לשיחת ייעוץ');
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
  };

  // Get translated project title/subtitle
  const getProjectTitle = (project) => {
    const titles = t.projectsConfigTitles[project.id];
    return titles ? titles.title : project.title;
  };
  const getProjectSubtitle = (project) => {
    const titles = t.projectsConfigTitles[project.id];
    return titles ? titles.subtitle : project.subtitle;
  };

  // טעינת thumbnails בהתחלה
  useEffect(() => {
    const loadAllThumbnails = async () => {
      const projectsWithThumbnails = await Promise.all(
        projectsConfig.map(async (config) => {
          try {
            const thumbnail = await loadThumbnail(
              config.folderName,
              config.thumbnailIndex
            );

            return {
              ...config,
              thumbnail,
              mainImage: thumbnail,
              gallery: []
            };
          } catch (error) {
            console.error(`Failed to load thumbnail for ${config.title}:`, error);
            return {
              ...config,
              thumbnail: null,
              mainImage: null,
              gallery: []
            };
          }
        })
      );

      setProjects(projectsWithThumbnails);
    };

    loadAllThumbnails();
  }, []);

  // Intersection Observer לאנימציית כניסה של thumbnails
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setTimeout(() => {
              setVisibleThumbnails(prev => new Set([...prev, index]));
            }, index * 60);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -30px 0px' }
    );

    thumbnailRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [projects]);

  // טעינת גלריה מלאה כשפותחים פרויקט
  const loadFullGallery = async (project) => {
    if (isLoadingGallery) return;

    setIsLoadingGallery(true);

    try {
      const gallery = await loadProjectGallery(
        project.folderName,
        project.imageCount
      );

      setProjectGallery(gallery);
      setCurrentImageIndex(0);
    } catch (error) {
      console.error('Failed to load gallery:', error);
      setProjectGallery([]);
    } finally {
      setIsLoadingGallery(false);
    }
  };

  // פתיחת פרויקט
  const openProject = (project) => {
    setSelectedProject(project);
    loadFullGallery(project);
    document.body.style.overflow = 'hidden';
  };

  // סגירת פרויקט
  const closeProject = () => {
    document.body.style.overflow = '';
    setSelectedProject(null);
    setProjectGallery([]);
    setIsGalleryOpen(false);
  };

  // Scroll reset כש-modal נפתח
  useEffect(() => {
    if (selectedProject && modalRef.current) {
      modalRef.current.scrollTop = 0;
    }
  }, [selectedProject]);

  // Body scroll lock
  useEffect(() => {
    if (selectedProject) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
      document.body.setAttribute('data-scroll-position', scrollY.toString());
    } else {
      const scrollPosition = document.body.getAttribute('data-scroll-position');
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      document.body.removeAttribute('data-scroll-position');
      if (scrollPosition) {
        window.scrollTo(0, parseInt(scrollPosition, 10));
      }
    }
  }, [selectedProject]);

  // פתיחת lightbox
  const openGallery = (index = 0) => {
    setCurrentImageIndex(index);
    setIsGalleryOpen(true);
  };

  const closeGallery = () => {
    setIsGalleryOpen(false);
  };

  // ניווט בגלריה
  const nextImage = () => {
    if (projectGallery.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === projectGallery.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (projectGallery.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? projectGallery.length - 1 : prev - 1
      );
    }
  };

  // טיפול במקלדת
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isGalleryOpen) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.key === 'ArrowRight' ? prevImage() : nextImage();
        } else if (e.key === 'Escape') {
          closeGallery();
        }
      } else if (selectedProject && e.key === 'Escape') {
        closeProject();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isGalleryOpen, selectedProject, projectGallery]);

  // Cleanup
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <section className={styles.projectsSection} ref={sectionRef} id="פרויקטים">
      {/* Background Decorations */}
      <div className={styles.bgDecoration}>
        <div className={styles.gridPattern}></div>
        <div className={styles.cornerAccent}></div>
        <div className={styles.cornerAccentBottom}></div>
      </div>

      {/* Title Section */}
      <header className={styles.titleSection}>
       
        <h2 className={styles.sectionTitle}>{t.moreProjects.title}</h2>
        <p className={styles.sectionSubtitle}>{t.moreProjects.subtitle}</p>
      </header>

      {/* Projects Grid - 4 columns */}
      <div className={styles.projectsGrid}>
        {projects.map((project, index) => (
          <div
            key={project.id}
            ref={el => thumbnailRefs.current[index] = el}
            data-index={index}
            className={`${styles.thumbnailCard} ${visibleThumbnails.has(index) ? styles.visible : ''}`}
            onClick={() => openProject(project)}
            style={{ '--index': index }}
          >
            <div className={styles.thumbnailImageWrapper}>
              {project.thumbnail ? (
                <LazyImage
                  src={project.thumbnail}
                  alt={project.title}
                  className={styles.thumbnailImage}
                  rootMargin="200px"
                />
              ) : (
                <div className={styles.imageSkeleton} />
              )}
              <div className={styles.thumbnailShine}></div>
            </div>

            <div className={styles.thumbnailContent}>
              <h3 className={styles.thumbnailTitle}>{getProjectTitle(project)}</h3>
              <p className={styles.thumbnailSubtitle}>{getProjectSubtitle(project)}</p>
              <div className={styles.thumbnailMeta}>
                <span>{project.imageCount} {t.moreProjects.photos}</span>
              </div>
            </div>

            <div className={styles.thumbnailOverlay}>
              <div className={styles.overlayContent}>
                <span className={styles.viewProject}>{t.moreProjects.viewProject}</span>
                <div className={styles.arrowIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M7 17L17 7M17 7H7M17 7V17"/>
                  </svg>
                </div>
              </div>
            </div>

            {/* Featured In Ribbon */}
            {projectFeaturedIn[project.id] && (
              <div className={styles.featuredRibbon}>
                <span className={styles.ribbonLabel}>{t.moreProjects.featuredLabel}</span>
                <div className={styles.ribbonMagazines}>
                  {projectFeaturedIn[project.id].join(', ')}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Consultation CTA Button */}
      <div className={styles.ctaSection}>
        <a 
          href={getWhatsAppLink()} 
          target="_blank" 
          rel="noopener noreferrer"
          className={styles.ctaButton}
        >
          <span className={styles.ctaText}>{t.moreProjects.ctaText}</span>
          <div className={styles.ctaArrow}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
          </div>
        </a>
      </div>

      {/* Project Modal */}
      {selectedProject && (
        <div className={styles.modalBackdrop} ref={modalRef}>
          <div className={styles.modal}>
            {/* Close Button */}
            <button className={styles.closeBtn} onClick={closeProject} aria-label="סגור">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>

            {/* Modal Hero */}
            <div className={styles.modalHero}>
              {selectedProject.mainImage ? (
                <img
                  src={selectedProject.mainImage}
                  alt={selectedProject.title}
                  className={styles.heroImage}
                loading="lazy" decoding="async" />
              ) : (
                <div className={styles.imageSkeleton} />
              )}
              <div className={styles.heroOverlay}></div>
              
              {/* Featured In Ribbon in Modal */}
              {projectFeaturedIn[selectedProject.id] && (
                <div className={styles.modalFeaturedRibbon}>
                  <span className={styles.modalRibbonLabel}>{t.moreProjects.featuredLabel}</span>
                  <div className={styles.modalRibbonMagazines}>
                    {projectFeaturedIn[selectedProject.id].join(', ')}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Content */}
            <div className={styles.modalContent}>
              <div className={styles.modalHeader}>
                <div className={styles.modalMeta}>
                  <span>{selectedProject.imageCount} {t.moreProjects.photos}</span>
                </div>
                <h2 className={styles.modalTitle}>{getProjectTitle(selectedProject)}</h2>
                <p className={styles.modalSubtitle}>{getProjectSubtitle(selectedProject)}</p>
              </div>

              {/* Gallery Section */}
              <div className={styles.gallerySection}>
                <h4 className={styles.gallerySectionTitle}>
                  <span className={styles.galleryTitleLine}></span>
                  {t.moreProjects.photoGallery}
                  <span className={styles.galleryTitleLine}></span>
                </h4>

                {isLoadingGallery ? (
                  <div className={styles.galleryLoader}>
                    <div className={styles.spinner} />
                    <p>{t.moreProjects.loadingGallery}</p>
                  </div>
                ) : (
                  <div className={styles.modalGallery}>
                    {projectGallery.map((img, idx) => (
                      <div
                        key={idx}
                        className={styles.galleryThumb}
                        onClick={() => openGallery(idx)}
                      >
                        <LazyImage
                          src={img}
                          alt={`${selectedProject.title} ${idx + 1}`}
                          rootMargin="300px"
                        />
                        <div className={styles.thumbOverlay}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"/>
                          </svg>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* WhatsApp CTA inside Modal */}
                <div className={styles.modalCtaSection}>
                  <a 
                    href={getWhatsAppLink(selectedProject.title)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={styles.modalCtaButton}
                  >
                    <span className={styles.modalCtaText}>{t.moreProjects.modalCtaText}</span>
                    <div className={styles.modalCtaArrow}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 5l-7 7 7 7"/>
                      </svg>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full Gallery Lightbox */}
      {isGalleryOpen && selectedProject && projectGallery.length > 0 && (
        <div className={styles.galleryOverlay}>
          <button className={styles.galleryCloseBtn} onClick={closeGallery} aria-label="סגור גלריה">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>

          <div className={styles.galleryViewer}>
            <img
              src={projectGallery[currentImageIndex]}
              alt={`${selectedProject.title} - עיצוב פנים - תמונה ${currentImageIndex + 1}`}
              className={styles.galleryMainImage}
            loading="lazy" decoding="async" />
          </div>

          <button className={`${styles.galleryNavBtn} ${styles.navPrev}`} onClick={prevImage} aria-label="תמונה קודמת">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
          <button className={`${styles.galleryNavBtn} ${styles.navNext}`} onClick={nextImage} aria-label="תמונה הבאה">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>

          <div className={styles.galleryProgress}>
            <div className={styles.progressTrack}>
              {projectGallery.map((_, idx) => (
                <div
                  key={idx}
                  className={`${styles.progressDot} ${idx === currentImageIndex ? styles.progressActive : ''}`}
                  onClick={() => setCurrentImageIndex(idx)}
                />
              ))}
            </div>
            <span className={styles.progressCounter}>
              {currentImageIndex + 1} / {projectGallery.length}
            </span>
          </div>

          <div className={styles.galleryThumbs}>
            {projectGallery.map((img, idx) => (
              <div
                key={idx}
                className={`${styles.thumbItem} ${idx === currentImageIndex ? styles.thumbActive : ''}`}
                onClick={() => setCurrentImageIndex(idx)}
              >
                <img src={img} alt={`${selectedProject.title} - תמונה ${idx + 1}`} loading="lazy" decoding="async" />
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default MoreProjects;